/**
 * 千葉県の背景ツアー「千葉めぐり」コンテンツ。
 *
 * 軸は北海道・山形と同じく「季節」。5月の棚田から始めて、
 * 1年かけて翌年4月の菜の花へ戻る。春=朝 → 夏=昼 → 秋=夕 → 冬=夜。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 「濃溝の滝」と「亀岩の洞窟」を同一視しない。正式名称は「清水渓流広場」。
 *  - 屏風ケ浦の「東洋のドーバー」は俗称。使わない。
 *  - 九十九里浜の「頼朝が1里ごとに矢を立てた」は伝承。断定しない。
 *  - 犬吠埼を「日本で最初に初日の出が見られる場所」と断定しない。
 *  - 鋸山を「日本遺産」と書かない（富津市公式の表記は「日本遺産候補地域」）。
 *  - 日本寺大仏の高さは公式資料で確認できなかったので数値を書かない。
 *  - 野島埼灯台は関東大震災後に再建された現在の姿で描く。
 *  - 養老渓谷は遊歩道に通行規制がある。
 */

import type { StageTour } from '../types';

export const chibaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'oyama-senmaida',
      name: '大山千枚田',
      area: '鴨川市・5月の朝',
      image: '/assets/scenery/chiba/scenes/01_oyama_senmaida.webp',
      hotspots: [
        {
          id: 'senmaida-maisu',
          x: 0.48,
          y: 0.44,
          title: 'およそ370枚',
          body: 'およそ370枚の大小さまざまな田が、段々に連なっています。平成14年に千葉県の名勝に指定されました。',
          source: 'https://www.city.kamogawa.lg.jp/site/shiryoukan/990.html',
          mapQuery: '大山千枚田',
        },
        {
          id: 'senmaida-tanada',
          x: 0.74,
          y: 0.3,
          title: '東京から一番近い棚田として知られる',
          body: '平成11年に農林水産省の「日本の棚田百選」、令和4年に「つなぐ棚田遺産」に選ばれています。10月下旬から1月上旬には1万本のLEDをともす「棚田のあかり」が行われます。',
          source: 'https://www.city.kamogawa.lg.jp/site/kamogawa-kanko/816.html',
          mapQuery: '大山千枚田 棚田倶楽部',
        },
      ],
    },
    {
      id: 'nojimazaki',
      name: '野島埼灯台',
      area: '南房総市・6月の午前',
      image: '/assets/scenery/chiba/scenes/02_nojimazaki.webp',
      hotspots: [
        {
          id: 'nojimazaki-hatsuten',
          x: 0.5,
          y: 0.32,
          title: '房総半島のいちばん南',
          body: '明治2年12月18日（新暦1870年1月19日）初点灯。フランス人技師ヴェルニーの部下フロランが設計しました。灯りは17.0海里、およそ31キロ先まで届きます。',
          source: 'https://www.kaiho.mlit.go.jp/03kanku/chiba/toudai/100_todai.html',
          mapQuery: '野島埼灯台',
        },
        {
          id: 'nojimazaki-saiken',
          x: 0.26,
          y: 0.5,
          title: 'いまの塔は二代目',
          body: '初代は高さ30メートルの八角形のレンガ造でしたが、大正12年の関東大震災で倒れました。大正14年8月15日に復旧し、いま立っているのはそのときの塔です。',
          source: 'https://www.kaiho.mlit.go.jp/03kanku/chiba/toudai/100_todai.html',
          mapQuery: '野島崎',
        },
      ],
    },
    {
      id: 'kujukurihama',
      name: '九十九里浜',
      area: '山武市・7月の昼',
      image: '/assets/scenery/chiba/scenes/03_kujukurihama.webp',
      hotspots: [
        {
          id: 'kujukuri-nagasa',
          x: 0.5,
          y: 0.5,
          title: '刑部岬から太東岬まで66キロ',
          body: '北の刑部岬（旭市）から南の太東岬（いすみ市）まで、66キロメートルの海岸が続きます。',
          source: 'https://www.city.sammu.lg.jp/sammu-kids/donna/page002161.html',
          relatedItemId: 'iwashi-dangojiru',
          mapQuery: '九十九里浜',
        },
        {
          id: 'kujukuri-chimei',
          x: 0.26,
          y: 0.62,
          title: '「九十九」を意識した地名',
          body: '九十九里町、大網白里、白子と、九十九を意識した地名が並びます。「白子」の「白」は、百に「一」が足りないことから九十九を指すといわれます。名の由来には後世の作為もうかがえるとされています。',
          source: 'https://www.city.sammu.lg.jp/sammu-kids/donna/page002161.html',
          mapQuery: '九十九里町',
        },
      ],
    },
    {
      id: 'nokogiriyama',
      name: '鋸山 地獄のぞき',
      area: '富津市・8月の昼',
      image: '/assets/scenery/chiba/scenes/04_nokogiriyama.webp',
      hotspots: [
        {
          id: 'nokogiri-jigoku',
          x: 0.54,
          y: 0.3,
          title: '石切場の跡の展望台',
          body: '地獄のぞきは、かつての石切場の跡にある展望台です。山には、石を切り出したあとの巨大な直壁がいまも残っています。',
          source: 'https://www.city.futtsu.lg.jp/0000007892.html',
          mapQuery: '鋸山 地獄のぞき',
        },
        {
          id: 'nokogiri-name',
          x: 0.26,
          y: 0.5,
          title: 'ギザギザの山の形',
          body: '標高329メートル。ギザギザとした山の形は、まさにノコギリです。',
          source: 'https://www.city.futtsu.lg.jp/0000007892.html',
          mapQuery: '鋸山',
        },
        {
          id: 'nokogiri-rakan',
          x: 0.76,
          y: 0.54,
          title: '千五百羅漢',
          body: '名工・大野甚五郎英令が20年の歳月をかけて、千五百羅漢の石像群をつくり上げました。鋸山で切り出された房州石は凝灰質砂岩で、横浜港などの礎石となり、日本の近代化を支えました。',
          source: 'https://www.city.futtsu.lg.jp/0000007892.html',
          mapQuery: '日本寺 千五百羅漢',
        },
      ],
    },
    {
      id: 'kameiwa-dokutsu',
      name: '亀岩の洞窟',
      area: '君津市・9月の早朝',
      image: '/assets/scenery/chiba/scenes/05_kameiwa_dokutsu.webp',
      hotspots: [
        {
          id: 'kameiwa-heart',
          x: 0.5,
          y: 0.42,
          title: '光と水面でハートになる',
          body: '川廻しの洞窟から差しこむ光が水面に反射して、ハートの形を描き出します。そう見えるのは3月と9月のお彼岸のころの早朝がよいとされています。',
          source: 'https://www.city.kimitsu.lg.jp/site/kanko/2259.html',
          mapQuery: '亀岩の洞窟 清水渓流広場',
        },
        {
          id: 'kameiwa-name',
          x: 0.24,
          y: 0.6,
          title: '正式には「清水渓流広場」',
          body: 'この場所の正式名称は清水渓流広場で、濃溝の滝と亀岩の洞窟は別のものです。有名な写真に写っているのは洞窟のほう。駐車場は無料で3か所、あわせておよそ130台。',
          source: 'https://www.city.kimitsu.lg.jp/site/kanko/2259.html',
          mapQuery: '清水渓流広場',
        },
        {
          id: 'kameiwa-chui',
          x: 0.76,
          y: 0.32,
          title: '洞窟の正面は落石に注意',
          body: '君津市は、ハート形が見やすい洞窟の正面付近は落石や崖崩れのおそれがあること、マムシや山ビルが出ることを案内しています。足もとに気をつけて。',
          source: 'https://www.city.kimitsu.lg.jp/site/kanko/2259.html',
          mapQuery: '清水渓流広場 駐車場',
        },
      ],
    },
    {
      id: 'byobugaura',
      name: '屏風ケ浦',
      area: '銚子市・10月の夕',
      image: '/assets/scenery/chiba/scenes/06_byobugaura.webp',
      hotspots: [
        {
          id: 'byobu-gake',
          x: 0.5,
          y: 0.4,
          title: '落差60メートルが10キロ続く',
          body: '銚子市の犬岩から旭市の刑部岬まで、落差60メートルの崖がおよそ10キロにわたって並びます。国の名勝および天然記念物です。',
          source: 'https://www.city.choshi.chiba.jp/edu/sg-guide/page210147.html',
          mapQuery: '屏風ケ浦',
        },
        {
          id: 'byobu-chisou',
          x: 0.75,
          y: 0.28,
          title: '300万年前からの地層',
          body: 'およそ300万年前から40万年前に積もった犬吠層群の上に、香取層と関東ローム層が載っています。火山灰の層などやわらかい部分が多く、かつては年に50〜100センチの速さで削られていました。',
          source: 'https://www.city.choshi.chiba.jp/edu/sg-guide/page210147.html',
          mapQuery: '屏風ヶ浦 銚子ジオパーク',
        },
      ],
    },
    {
      id: 'yoro-keikoku',
      name: '養老渓谷 粟又の滝',
      area: '大多喜町・11月の午後',
      image: '/assets/scenery/chiba/scenes/07_yoro_keikoku.webp',
      hotspots: [
        {
          id: 'yoro-awamata',
          x: 0.5,
          y: 0.46,
          title: '落差30メートル、長さ100メートル',
          body: '落差およそ30メートル、長さおよそ100メートルにわたって、水しぶきをあげて流れ落ちます。',
          source: 'https://maruchiba.jp/spot/detail_10352.html',
          mapQuery: '粟又の滝',
        },
        {
          id: 'yoro-kouyou',
          x: 0.75,
          y: 0.28,
          title: '紅葉は11月下旬から',
          body: 'モミジのほか、ウルシ・クヌギ・ナラが色づきます。見ごろは例年11月下旬から12月上旬ごろです。',
          source: 'https://maruchiba.jp/spot/detail_10271.html',
          mapQuery: '養老渓谷',
        },
        {
          id: 'yoro-tsuko',
          x: 0.24,
          y: 0.6,
          title: '遊歩道は一部が通行規制中',
          body: '台風13号の影響で、滝めぐり遊歩道は上流（粟又の滝）側から100メートル地点で折り返しとなり、下流側からは立ち入り禁止です。粟又の滝は、滝のまわりを歩くことができます。',
          source: 'https://maruchiba.jp/spot/detail_10352.html',
          mapQuery: '養老渓谷 滝めぐり遊歩道',
        },
      ],
    },
    {
      id: 'inubosaki',
      name: '犬吠埼灯台',
      area: '銚子市・12月の夜',
      image: '/assets/scenery/chiba/scenes/08_inubosaki.webp',
      hotspots: [
        {
          id: 'inubo-renga',
          x: 0.5,
          y: 0.3,
          title: 'レンガ19万枚',
          body: '明治7年（1874年）点灯。イギリス人技師ヘンリー・ブラントンの設計で、香取郡高岡村（いまの成田市高岡）産の原料で焼いたレンガおよそ19万枚を使っています。総高31メートル。',
          source: 'https://www.city.choshi.chiba.jp/edu/sg-guide/page210157.html',
          mapQuery: '犬吠埼灯台',
        },
        {
          id: 'inubo-muteki',
          x: 0.74,
          y: 0.5,
          title: '霧笛舎もいっしょに重要文化財',
          body: '灯台と、明治43年建造の旧霧笛舎、明治7年建造の旧倉庫の3件が、令和2年12月23日に国の重要文化財に指定されました。旧霧笛舎は、かまぼこ形の屋根から突き出したラッパが特徴です。',
          source: 'https://www.city.choshi.chiba.jp/edu/sg-guide/page210157.html',
          mapQuery: '犬吠埼 旧霧笛舎',
        },
      ],
    },
    {
      id: 'naritasan',
      name: '成田山新勝寺',
      area: '成田市・1月の朝',
      image: '/assets/scenery/chiba/scenes/09_naritasan.webp',
      hotspots: [
        {
          id: 'narita-kaisan',
          x: 0.5,
          y: 0.34,
          title: '940年の開山',
          body: '940年、平将門の乱の平定を祈願して、寛朝大僧正が京都から不動明王を奉安して開いた寺です。',
          source: 'https://maruchiba.jp/spot/detail_10145.html',
          mapQuery: '成田山新勝寺',
        },
        {
          id: 'narita-sanjunoto',
          x: 0.74,
          y: 0.26,
          title: '竜と十六羅漢の三重塔',
          body: '三重塔は正徳2年（1712年）に建てられ、相輪の頂上まで約25メートルあります。各層の尾垂木の先端は竜の丸彫りで飾られ、脇間の板壁には「十六羅漢」の彫刻がめぐらされています。',
          source: 'https://www.pref.chiba.lg.jp/kyouiku/bunkazai/bunkazai/n111-025-3.html',
          mapQuery: '成田山新勝寺 三重塔',
        },
        {
          id: 'narita-sanpai',
          x: 0.24,
          y: 0.52,
          title: '正月三が日に300万人',
          body: '正月の三が日におよそ300万人、年間ではおよそ1,000万人が参詣します。門前からは表参道の賑わいが続きます。',
          source: 'https://maruchiba.jp/spot/detail_10145.html',
          mapQuery: '成田山表参道',
        },
      ],
    },
    {
      id: 'kominato-nanohana',
      name: '小湊鐵道の菜の花',
      area: '市原市・4月の昼',
      image: '/assets/scenery/chiba/scenes/10_kominato_nanohana.webp',
      hotspots: [
        {
          id: 'kominato-mikoro',
          x: 0.5,
          y: 0.42,
          title: '見ごろは3月下旬から4月中旬',
          body: '線路の両脇を黄色い菜の花が埋めます。上総川間・上総鶴舞・里見・飯給・月崎・上総大久保・養老渓谷の各駅まわりが見どころで、とくに高滝駅から養老渓谷駅のあいだ。',
          source: 'https://maruchiba.jp/event/detail_10844.html',
          mapQuery: '小湊鐵道 飯給駅',
        },
        {
          id: 'kominato-bosou',
          x: 0.76,
          y: 0.28,
          title: '房総横断鉄道',
          body: '小湊鐵道といすみ鉄道は上総中野駅でつながり、五井駅から大原駅まで65.9キロを結んでいます。房総半島を横に渡る鉄道です。',
          source: 'https://www.kominato.co.jp/tourism/transboso/',
          mapQuery: '上総中野駅',
        },
        {
          id: 'kominato-manner',
          x: 0.24,
          y: 0.6,
          title: '土地の人がつくる景色',
          body: '石神の菜の花畑も飯給駅の水鏡も、土地の持ち主の方々の尽力で生まれている景色です。撮影のときは、線路内や立ち入り禁止の場所、私有地に無断で入らないようにしてください。',
          source: 'https://maruchiba.jp/feature/detail_368.html',
          mapQuery: '小湊鐵道 高滝駅',
        },
      ],
    },
  ],
};
